import Tooltip from '@mui/material/Tooltip'
import { Fragment, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import BucketCreatePopup from 'routes/publishing/routes/buckets/components/BucketCreatePopup'
import BucketUpdatePopup from 'routes/publishing/routes/buckets/components/BucketUpdatePopup'
import { getBuckets } from 'services/post/actions'
import { failedPostsStatusSelector } from 'services/post/selectors'
import { Bucket } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import styles from '../AppNavigation.pcss'
import { AppNavLink } from '../AppNavLink'
import BucketsNavMenu from './BucketsNavMenu'
import { PlansNavigation } from './PlansNavigation'
import PostProfilesSelector from './PostProfilesSelector'

interface PublishingNavProps {
  hideProfiles?: boolean
  activeView: 'calendar' | 'posts' | 'plans' | 'history'
}

export function PublishingNav(props: PublishingNavProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const location = useLocation()
  const navigate = useNavigate()
  const [openSubnav, setOpenSubnav] = useState<null | 'queue' | 'buckets'>(null)
  const [selectedUpdateBucket, setSelectedUpdateBucket] = useState<Bucket | null>(null)
  const [bucketCreatePopupOpen, setBucketCreatePopupOpen] = useState<boolean>(false)
  const failedPostsStatus = useSelector(failedPostsStatusSelector)

  useEffect(() => {
    if (location.pathname.startsWith('/posts/buckets')) {
      setOpenSubnav('buckets')
    } else if (location.pathname.startsWith('/posts/planned')) {
      setOpenSubnav('queue')
    }
  }, [location.pathname])

  const toggleItemExpanded = (item: 'queue' | 'buckets') => () => {
    setOpenSubnav(current => current === item ? null : item)
  }

  const closeBucketUpdatePopup = (updated?: boolean) => {
    setSelectedUpdateBucket(null)
    if (updated) {
      dispatch(getBuckets(true))
    }
  }

  const openBucketCreatePopup = () => {
    setBucketCreatePopupOpen(true)
  }

  const closeBucketCreatePopup = () => {
    setBucketCreatePopupOpen(false)
  }

  const onBucketCreated = (b: Bucket) => {
    closeBucketCreatePopup()
    navigate(`/posts/buckets/${b.id}/posts`)
  }

  const showFailedPostsBadge = failedPostsStatus.hasFailedPosts

  return (
    <nav>
      <h1 className={styles['subnav-title']}>
        <FormattedMessage id={`app.nav.title-${props.activeView}`} />
      </h1>
      {props.activeView === 'posts' && (
        <Fragment>
          <AppNavLink
            to={`/posts/planned/all`}
            label={<FormattedMessage id="app.nav.publishing-planned-planned" />}
          />
          <BucketsNavMenu
            expanded={openSubnav === 'buckets'}
            onCreateClick={openBucketCreatePopup}
            onEditBucketClick={setSelectedUpdateBucket}
            onToggleExpanded={toggleItemExpanded('buckets')}
          />
          <AppNavLink
            to="/posts/planned/drafts"
            label={<FormattedMessage id="app.nav.publishing-drafts" />}
          />
        </Fragment>
      )}
      {props.activeView === 'history' && (
        <Fragment>
          <AppNavLink
            label={<FormattedMessage id="app.nav.publishing-posted-posts" />}
            to="/history/posted"
            forceExpanded
          />
          <AppNavLink
            label={<FormattedMessage id="app.nav.publishing-archived-posts" />}
            to="/history/archived"
            forceExpanded
          />
          <AppNavLink
            label={(
              <span className={styles['label-failed']}>
                <FormattedMessage id="app.nav.publishing-failed-posts" />
                {showFailedPostsBadge && (
                  <Tooltip title="You have failed posts">
                    <div className={styles['notitication-badge']}></div>
                  </Tooltip>
                )}
              </span>
            )}
            to="/history/failed"
          />
        </Fragment>
      )}
      {!props.hideProfiles && props.activeView !== 'plans' && (
        <PostProfilesSelector hidden={false} activeView={props.activeView} />
      )}
      {selectedUpdateBucket && (
        <BucketUpdatePopup bucket={selectedUpdateBucket} onClose={closeBucketUpdatePopup} />
      )}
      <BucketCreatePopup open={bucketCreatePopupOpen} onClose={closeBucketCreatePopup} onCreated={onBucketCreated} />
      {props.activeView === 'plans' && <PlansNavigation />}
      <div data-buckets-nav></div>
    </nav>
  )
}

export default PublishingNav
